import React, { useMemo } from "react";
import { Fade, Paper, useTheme } from "@mui/material";
import { ResponsiveBar } from "@nivo/bar";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../app/helpers";
import { RootState } from "../../../app/rootReducer";
import { buildWeekdayChartData } from "./weekday-report-chart.functions";

interface WeekdayBarsProps {
  data: any[];
}
const WeekdayBars: React.FC<WeekdayBarsProps> = ({ data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { currentFloors, selectedZoneOrWorkplace, selectableEntities } = useSelector(
    (state: RootState) => state.report
  );

  const commonProps = { margin: { top: 30, right: 20, bottom: 50, left: 70 }, padding: 0.2 };

  const getLabelName = (index: number) =>
    selectableEntities?.[0]?.floors?.find(e => e.inventoryId === currentFloors[index]?.id)?.name ??
    "";

  const weekdayData = useMemo(
    () => buildWeekdayChartData(data, currentFloors, selectedZoneOrWorkplace, getLabelName),
    [data, currentFloors, selectedZoneOrWorkplace, selectableEntities]
  );

  const responsiveBarTooltip = (id: string | number, value: number) => {
    return (
      <strong style={{ color: "black", background: "#ffffff", padding: 5, borderRadius: 4 }}>
        {`${t(id.toString())}` + ": " + (value * 100).toFixed(0)}%
      </strong>
    );
  };

  const getTickValues = () => {
    const checkMaxValue = weekdayData.every(weekday => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { id, ...data } = weekday;
      return Object.values(data).every(value => value < 0.01);
    });

    if (checkMaxValue) return { tickValues: 2 };
    return { tickValues: 4 };
  };

  return (
    <Fade mountOnEnter unmountOnExit in={weekdayData?.length > 0}>
      <Paper
        data-testid="weekday-report-chart-paper"
        sx={{ width: "100%", height: 380, marginTop: 2, marginBottom: 2 }}
        variant={"outlined"}
      >
        <ResponsiveBar
          {...commonProps}
          data={weekdayData.map(singleData => ({
            id: singleData.id ?? "",
            monday: singleData.monday?.toFixed(2) ?? "",
            tuesday: singleData.tuesday?.toFixed(2) ?? "",
            wednesday: singleData.wednesday?.toFixed(2) ?? "",
            thursday: singleData.thursday?.toFixed(2) ?? "",
            friday: singleData.friday?.toFixed(2) ?? "",
            saturday: singleData.saturday?.toFixed(2) ?? "",
            sunday: singleData.sunday?.toFixed(2) ?? ""
          }))}
          groupMode="grouped"
          keys={["monday", "tuesday", "wednesday", "thursday", "friday"]}
          tooltip={({ id, value }) => responsiveBarTooltip(id, value)}
          theme={{
            tooltip: { container: { background: "#ffffff", borderRadius: 4 } },
            grid: { line: { stroke: "#585858", strokeWidth: 1 } },
            axis: {
              ticks: { text: { fontSize: 10, fill: theme.palette.text.primary } },
              legend: { text: { fill: theme.palette.text.primary } }
            }
          }}
          enableLabel={false}
          enableGridX={false}
          enableGridY={true}
          axisLeft={{
            tickValues: getTickValues().tickValues,
            format: tick => Number(tick) * 100 + "%"
          }}
          axisBottom={{
            legend: t("capacity per weekday"),
            legendOffset: 36,
            legendPosition: "middle"
          }}
          valueScale={{ type: "linear" }}
          indexScale={{ type: "band", round: true }}
        />
      </Paper>
    </Fade>
  );
};
export default WeekdayBars;
